import React, { useLayoutEffect } from "react";
import { removeStaticHeadTags } from "../utils/staticHead";

/**
 * The page's head tags. React 19 places a <title>, <meta> or <link>
 * rendered anywhere in the tree into <head>, so this component renders
 * them as plain elements ; the static copies index.html carries for
 * crawlers are removed as this component first commits
 * (src/client/utils/staticHead.js).
 */
export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = "website",
  // "index, follow" unless a page says otherwise - rendered
  // UNCONDITIONALLY so every page carries exactly one robots value : the
  // static tag in index.html is removed with the other marked tags when
  // this component commits, and this one is what remains. Two robots metas with opposite
  // directives is the exact contradiction the popup's noindex must not
  // ship (2026-08-28).
  robots = "index, follow",
}) {
  // The same suffix build/generate-static-pages.js writes into the
  // static files (its BRAND_SUFFIX check), so the title a crawler reads
  // before hydration and the title rendered here after it are one
  // string. They differed until 2026-08-28 ("| SSL Tools" at runtime
  // against "| Trustico® SSL Tools" on disk), which is also why the head
  // library of the time could not reconcile the static og:title and
  // appended a second one.
  const siteName = "Trustico® SSL Tools";
  const fullTitle = title ? `${title} | ${siteName}` : siteName;

  // The static copies leave when this component first commits, in a layout
  // effect : React has placed its own tags by then and the browser has not
  // painted yet, so a page never shows two sets, and a URL that renders no
  // <SEO> (nothing in App.jsx matches it) keeps the static head it always
  // had. Later mounts find nothing marked and do nothing.
  useLayoutEffect(() => {
    removeStaticHeadTags(document);
  }, []);

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Open Graph tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={siteName} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      {/* Twitter Card tags */}
      {/* summary_large_image since 2026-08-28 : the static head carries
          the operator's 1200x630 card (og:image / twitter:image in
          index.html, never rendered here, so it survives the boot-time
          removal), and the card type must match it. */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
    </>
  );
}
